export const WEEK_COUNT = 4;

export const SOURCE_DEFS = [
  { id: 'meta', name: 'Meta' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'google_search', name: 'Google Search' },
  { id: 'yandex_search', name: 'Яндекс Поиск' },
  { id: 'google_display', name: 'Google Display' },
  { id: 'yandex_display', name: 'Яндекс РСЯ' },
  { id: 'other', name: 'Другое' },
];

export const DEFAULT_ACTIVE_SOURCES = ['meta', 'google_search'];

const emptyWeeks = () => Array.from({ length: WEEK_COUNT }, () => ({ spend: 0, leads: 0, revenue: 0 }));

export const buildDefaultRomiData = () => {
  const base = {
    meta: [
      { spend: 80000, leads: 28, revenue: 560000 },
      { spend: 90000, leads: 32, revenue: 640000 },
      { spend: 95000, leads: 30, revenue: 600000 },
      { spend: 100000, leads: 35, revenue: 700000 },
    ],
    google_search: [
      { spend: 60000, leads: 18, revenue: 450000 },
      { spend: 65000, leads: 20, revenue: 500000 },
      { spend: 70000, leads: 19, revenue: 475000 },
      { spend: 75000, leads: 22, revenue: 550000 },
    ],
    tiktok: emptyWeeks(),
    yandex_search: emptyWeeks(),
    google_display: emptyWeeks(),
    yandex_display: emptyWeeks(),
    other: emptyWeeks(),
  };
  return base;
};

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
};

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString(locale)} ₸`;
};

export const formatCount = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString(locale);
};

export const calcWeekMetrics = (spend, leads, revenue) => {
  const s = clampNumber(spend, 0);
  const l = clampNumber(leads, 0);
  const r = clampNumber(revenue, 0);
  const cpl = l > 0 ? s / l : null;
  const romi = s > 0 ? ((r - s) / s) * 100 : null;
  return { spend: s, leads: l, revenue: r, cpl, romi };
};

export const calculateRomiTracker = (activeSources, data) => {
  const weeks = Array.from({ length: WEEK_COUNT }, (_, weekIdx) => {
    let spend = 0;
    let leads = 0;
    let revenue = 0;

    activeSources.forEach((sourceId) => {
      const cell = data[sourceId]?.[weekIdx] || { spend: 0, leads: 0, revenue: 0 };
      spend += clampNumber(cell.spend, 0);
      leads += clampNumber(cell.leads, 0);
      revenue += clampNumber(cell.revenue, 0);
    });

    return { week: weekIdx + 1, ...calcWeekMetrics(spend, leads, revenue) };
  });

  const totals = weeks.reduce(
    (acc, w) => ({
      spend: acc.spend + w.spend,
      leads: acc.leads + w.leads,
      revenue: acc.revenue + w.revenue,
    }),
    { spend: 0, leads: 0, revenue: 0 },
  );

  const period = calcWeekMetrics(totals.spend, totals.leads, totals.revenue);

  return { weeks, period };
};

export const calculateSourceTotals = (activeSources, data) =>
  activeSources
    .map((sourceId) => {
      let spend = 0;
      let leads = 0;
      let revenue = 0;

      for (let weekIdx = 0; weekIdx < WEEK_COUNT; weekIdx += 1) {
        const cell = data[sourceId]?.[weekIdx] || { spend: 0, leads: 0, revenue: 0 };
        spend += clampNumber(cell.spend, 0);
        leads += clampNumber(cell.leads, 0);
        revenue += clampNumber(cell.revenue, 0);
      }

      return { id: sourceId, ...calcWeekMetrics(spend, leads, revenue) };
    })
    .filter((row) => row.spend > 0);

const sourceLabel = (id) => SOURCE_DEFS.find((s) => s.id === id)?.name || id;

export const getRomiChannelInsights = (activeSources, data) => {
  const rows = calculateSourceTotals(activeSources, data);
  const insights = [];

  if (rows.length === 0) {
    return [{ text: 'Заполните расход на рекламу хотя бы по одному каналу.' }];
  }

  const withRomi = rows.filter((r) => r.romi != null && Number.isFinite(r.romi));
  const bestRomi = withRomi.length
    ? withRomi.reduce((best, row) => (row.romi > best.romi ? row : best), withRomi[0])
    : null;
  const worstRomi = withRomi.length
    ? withRomi.reduce((worst, row) => (row.romi < worst.romi ? row : worst), withRomi[0])
    : null;

  const negative = withRomi.filter((r) => r.romi < 0);

  if (bestRomi && rows.length > 1) {
    insights.push({
      text: `${sourceLabel(bestRomi.id)} — лучший ROMI за период (${Math.round(bestRomi.romi)}%). Сильнее всего окупает рекламу.`,
    });
  }

  if (worstRomi && rows.length > 1 && worstRomi.id !== bestRomi?.id) {
    if (worstRomi.romi < 0) {
      insights.push({
        text: `${sourceLabel(worstRomi.id)} — худший канал, ROMI ${Math.round(worstRomi.romi)}%. Реклама не окупается — присмотритесь: креативы, оффер, качество лидов.`,
      });
    } else {
      insights.push({
        text: `${sourceLabel(worstRomi.id)} — слабее остальных (ROMI ${Math.round(worstRomi.romi)}%). Проверьте воронку и качество лидов.`,
      });
    }
  }

  if (bestRomi && bestRomi.romi > 0) {
    insights.push({
      text: `Имеет смысл увеличить бюджет на ${sourceLabel(bestRomi.id)} — канал даёт лучший возврат.`,
    });
  }

  negative
    .filter((r) => r.id !== worstRomi?.id)
    .forEach((row) => {
      insights.push({
        text: `${sourceLabel(row.id)} в минусе (ROMI ${Math.round(row.romi)}%) — сократите расход или оптимизируйте до выхода в плюс.`,
      });
    });

  if (insights.length === 0) {
    insights.push({ text: 'По каналам данных мало для сравнения — заполните все недели.' });
  }

  return insights;
};

export const buildChartSeries = (weeks) => {
  const cplValues = weeks.map((w) => (w.cpl != null ? w.cpl : 0));
  const romiValues = weeks.map((w) => (w.romi != null ? w.romi : 0));

  const maxCpl = Math.max(...cplValues, 1);
  const romiAbs = Math.max(...romiValues.map(Math.abs), 1, 100);

  const width = 100;
  const height = 48;
  const padX = 4;
  const padY = 6;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const xAt = (i) => padX + (WEEK_COUNT <= 1 ? innerW / 2 : (i / (WEEK_COUNT - 1)) * innerW);
  const yCpl = (v) => padY + innerH - (v / maxCpl) * innerH;
  const yRomi = (v) => padY + innerH / 2 - (v / (romiAbs * 2)) * innerH;

  const cplPoints = cplValues.map((v, i) => `${xAt(i)},${yCpl(v)}`).join(' ');
  const romiPoints = romiValues.map((v, i) => `${xAt(i)},${yRomi(v)}`).join(' ');

  return { cplPoints, romiPoints, maxCpl, romiAbs };
};

export const getRomiInsight = (weeks) => {
  const valid = weeks.filter((w) => w.spend > 0 && w.romi != null);
  if (valid.length < 2) return null;

  const first = valid[0];
  const last = valid[valid.length - 1];
  const romiDelta = last.romi - first.romi;
  const cplDelta =
    first.cpl != null && last.cpl != null && first.cpl > 0
      ? ((last.cpl - first.cpl) / first.cpl) * 100
      : 0;

  if (last.romi < 0) {
    return { text: `На последней неделе ROMI ${Math.round(last.romi)}% — реклама не окупается.` };
  }

  if (romiDelta > 10) {
    return { text: `ROMI вырос на ${Math.round(romiDelta)} п.п. за период — хороший тренд.` };
  }

  if (cplDelta > 15) {
    return { text: `CPL вырос на ${Math.round(cplDelta)}% — проверьте качество лидов и ставки.` };
  }

  return { text: 'По неделям динамика ровная — смотрите выводы по каналам ниже.' };
};

export const HOW_STEPS = [
  { num: '1', title: 'Каждый пункт имеет вес.', text: '«Да» = 100% балла, «Частично» = 50%, «Нет» = 0%. Итог — сумма взвешенных баллов.' },
  { num: '2', title: 'Пороги готовности:', text: 'до 50% — рано, 50–79% — доработать, 80%+ — можно запускать тест.' },
  { num: '3', title: 'Список «Сделать в первую очередь»', text: 'показывает пункты с ответом «Нет» и «Частично» по приоритету веса.' },
];
